import { Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { PartnerSearchType } from '@evimo/types/enums/partner-search-type';
import { PartnerHeroCard } from '~/app/landing/sla/shared/components/PartnerHeroCard';
import { useEffectiveOrganizationName } from '~/app/landing/sla/shared/hooks/useEffectiveOrganizationName';
import { OemSlaReviewSummary } from '~/app/landing/sla/shared/components/SlaReviewSummary';
import { PartnerOrgAutocomplete } from '~/app/landing/sla/shared/PartnerOrgAutocomplete';
import type { PartnerOrgSelection } from '~/app/landing/sla/shared/types';
import type { OemPartnerFormValues } from '@/app/landing/sla/oems/OemPartnerModal/types';
import { OemPartnerRateMatrix } from '@/app/landing/sla/oems/OemPartnerModal/components/OemPartnerRateMatrix';
import { SLA_OEM_RATE_MATRIX_MAX_ROWS_CREATE } from '~/app/landing/sla/shared/sla.constants';

type Props = {
  organizationId: string;
  isEdit: boolean;
  step: number;
  selectedPartner: PartnerOrgSelection | null;
  onPartnerChange: (partner: PartnerOrgSelection | null) => void;
  contractSnapshot: OemPartnerFormValues | null;
};

export function OemPartnerModalBody({
  organizationId,
  isEdit,
  step,
  selectedPartner,
  onPartnerChange,
  contractSnapshot,
}: Props) {
  const { t } = useTranslation('sla');
  const operatorOrganizationName = useEffectiveOrganizationName(organizationId);

  return (
    <>
      {!isEdit && step === 0 && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            {t('stepper.partnerHint')}
          </Typography.Text>
          <PartnerOrgAutocomplete
            organizationId={organizationId}
            searchType={PartnerSearchType.OEM}
            placeholder={t('oems.partnerSearchPlaceholder')}
            partner={selectedPartner}
            onPartnerChange={onPartnerChange}
          />
        </Space>
      )}

      {!isEdit && step === 1 && (
        <PartnerHeroCard
          partner={selectedPartner}
          variant="oem"
          operatorOrganizationName={operatorOrganizationName}
        />
      )}

      {(isEdit || step >= 2) && step !== 3 && (
        <OemPartnerRateMatrix
          maxRateEntries={
            isEdit ? undefined : SLA_OEM_RATE_MATRIX_MAX_ROWS_CREATE
          }
        />
      )}

      {!isEdit && step === 3 && contractSnapshot && (
        <OemSlaReviewSummary
          partnerName={selectedPartner?.name ?? '—'}
          status={contractSnapshot.status}
          validFrom={contractSnapshot.validFrom}
          validTo={contractSnapshot.validTo ?? null}
          rateEntries={contractSnapshot.rateEntries}
        />
      )}
    </>
  );
}
